// PartBPage2/index.jsx - Part B Page 2 (Student Editor)
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useActivityContext } from '../index';
import { API_CONFIG } from '../../../../../config/api';

const PartBPage2 = () => {
  const { activity, userRole } = useActivityContext();
  const [studentFields, setStudentFields] = useState({
    project_schedule: {},
    resources_needed: [],
    roles_of_group_members: '',
    possible_barriers: [],
    awareness: {},
    sensitivity: {},
    creativity: {}
  });
  const [saveStatus, setSaveStatus] = useState('');

  useEffect(() => {
    if (activity?.my_response?.page_2) {
      setStudentFields(activity.my_response.page_2);
    }
  }, [activity]);

  const awarenessOptions = [
    { key: 'understand_purpose', label: 'I understand the purpose of the project.' },
    { key: 'read_understand_material', label: 'I could read and understand the resource material.' },
    { key: 'talk_about_things_known', label: 'I talk about things I know that are needed for the project.' },
    { key: 'identify_challenges', label: 'I identify challenges my group might face during the project.' },
    { key: 'enumerate_steps', label: 'I could enumerate and describe the steps (start to finish) required to do the project.' }
  ];

  const sensitivityOptions = [
    { key: 'listen_respect_ideas', label: 'I listen to my group\'s ideas and respect them.' },
    { key: 'collective_decisions', label: 'I try to make sure group decisions are taken collectively.' },
    { key: 'understand_all_aspects', label: 'I try to make sure that my peers understand all aspects of the project.' },
    { key: 'relate_to_objectives', label: 'I can meaningfully relate to the objectives of the project.' },
    { key: 'feel_joyous_contributing', label: 'I feel joyous in contributing to the project.' }
  ];

  const creativityOptions = [
    { key: 'different_ways_approach', label: 'I think of different ways to approach the task.' },
    { key: 'brainstorm_execution', label: 'I brainstorm about project execution and presentation.' },
    { key: 'think_different_resources', label: 'I think of different resources to be used in the project.' },
    { key: 'innovative_solutions', label: 'I come up with innovative solutions to mitigate the challenges.' },
    { key: 'relate_output_daily_life', label: 'I can think of new ideas to relate the output of the project in daily life.' }
  ];

  const saveData = async () => {
    try {
      setSaveStatus('Saving...');
      const token = localStorage.getItem('accessToken');
      const apiUrl = API_CONFIG.BASE_URL;
      await axios.put(
        `${apiUrl}/secondary/user/domain-activities/${activity.activity_id}/part-b/page/2/student-fields`,
        studentFields,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaveStatus('✓ Saved');
      setTimeout(() => setSaveStatus(''), 2000);
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus('Error');
    }
  };

  const handleScheduleChange = (day, value) => {
    setStudentFields(prev => ({
      ...prev,
      project_schedule: { ...prev.project_schedule, [`day_${day}`]: value }
    }));
  };

  const handleArrayChange = (field, text) => {
    const arr = text.split('\n').filter(x => x.trim());
    setStudentFields(prev => ({ ...prev, [field]: arr }));
  };

  const handleCheckboxChange = (category, key) => {
    setStudentFields(prev => ({
      ...prev,
      [category]: { ...prev[category], [key]: !prev[category]?.[key] }
    }));
  };

  const calculateTotal = (category) => {
    return Object.values(studentFields[category] || {}).filter(Boolean).length;
  };

  const renderCheckboxSection = (title, options, category) => {
    const total = calculateTotal(category);

    return (
      <div style={{ marginBottom: '16px' }}>
        <div style={{
          backgroundColor: '#4A90A4',
          color: 'white',
          padding: '8px 12px',
          fontWeight: 'bold',
          fontSize: '14px',
          marginBottom: '8px'
        }}>
          {title}
        </div>
        <div style={{
          backgroundColor: '#C5E3ED',
          padding: '12px'
        }}>
          {options.map(opt => (
            <div key={opt.key} style={{
              display: 'flex',
              alignItems: 'flex-start',
              marginBottom: '6px',
              fontSize: '15px',
              lineHeight: '1.4'
            }}>
              <input
                type="checkbox"
                checked={!!studentFields[category]?.[opt.key]}
                onChange={() => handleCheckboxChange(category, opt.key)}
                onBlur={saveData}
                style={{
                  width: '16px',
                  height: '16px',
                  marginRight: '8px',
                  marginTop: '1px',
                  flexShrink: 0,
                  cursor: 'pointer'
                }}
              />
              <span>{opt.label}</span>
            </div>
          ))}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            marginTop: '10px',
            fontSize: '13px',
            fontWeight: 'bold'
          }}>
            <span style={{ marginRight: '10px' }}>Total number of (✓):</span>
            <div style={{
              width: '30px',
              height: '30px',
              border: '2px solid #333',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontWeight: 'bold',
              fontSize: '14px'
            }}>
              {total}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      width: '100%',
      maxWidth: '1400px',
      minHeight: '80vh',
      margin: '0 auto',
      padding: '40px',
      backgroundColor: 'white',
      position: 'relative',
      fontFamily: "'Poppins', Arial, sans-serif",
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      borderRadius: '8px',
      boxSizing: 'border-box'
    }}>
      {/* Save Status */}
      {saveStatus && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          backgroundColor: saveStatus.includes('Error') ? '#f44336' : '#4caf50',
          color: 'white',
          padding: '8px 16px',
          borderRadius: '4px',
          zIndex: 1000
        }}>
          {saveStatus}
        </div>
      )}

      {/* Top Right Corner */}
      <div style={{
        position: 'absolute',
        top: 0,
        right: 0,
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '0 80px 80px 0',
        borderColor: 'transparent #F4824C transparent transparent'
      }} />

      <div style={{
        backgroundColor: '#F4824C',
        color: 'white',
        fontSize: '16px',
        fontWeight: 'bold',
        padding: '10px',
        textAlign: 'center',
        marginBottom: '20px'
      }}>
        Stage 1
      </div>

      {/* Project Schedule */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', gap: '0' }}>
          <div style={{
            backgroundColor: '#4A90A4',
            color: 'white',
            padding: '14px',
            fontWeight: 'bold',
            fontSize: '11px',
            flex: '0 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '110px'
          }}>
            Project schedule
          </div>
          <div style={{
            backgroundColor: '#C5E3ED',
            padding: '12px 16px',
            flex: 1,
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '8px',
            fontSize: '13px'
          }}>
            {[1, 2, 3, 4, 5].map(day => (
              <div key={day} style={{ display: 'flex', gap: '6px' }}>
                <strong style={{ minWidth: '45px' }}>Day {day}:</strong>
                <input
                  type="text"
                  value={studentFields.project_schedule?.[`day_${day}`] || ''}
                  onChange={(e) => handleScheduleChange(day, e.target.value)}
                  onBlur={saveData}
                  style={{
                    flex: 1,
                    border: 'none',
                    background: 'transparent',
                    fontSize: '13px',
                    padding: '2px'
                  }}
                  placeholder="-"
                />
              </div>
            ))}
            {[6, 7, 8, 9, 10].map(day => (
              <div key={day} style={{ display: 'flex', gap: '6px' }}>
                <strong style={{ minWidth: '45px' }}>Day {day}:</strong>
                <input
                  type="text"
                  value={studentFields.project_schedule?.[`day_${day}`] || ''}
                  onChange={(e) => handleScheduleChange(day, e.target.value)}
                  onBlur={saveData}
                  style={{
                    flex: 1,
                    border: 'none',
                    background: 'transparent',
                    fontSize: '13px',
                    padding: '2px'
                  }}
                  placeholder="-"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Three Column Layout */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '0.85fr 1fr 1fr',
        gap: '12px',
        marginBottom: '16px'
      }}>
        {/* Resources - Notepad Style */}
        <div>
          <div style={{
            backgroundColor: '#8B6F47',
            color: '#F5E6D3',
            padding: '8px',
            fontWeight: 'bold',
            fontSize: '11px',
            textAlign: 'center',
            marginBottom: '4px',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px'
          }}>
            Resources needed
          </div>
          <div style={{
            backgroundColor: '#F5E6D3',
            border: '2px solid #8B6F47',
            borderTop: 'none',
            padding: '12px',
            position: 'relative',
            minHeight: '160px'
          }}>
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'repeating-linear-gradient(transparent, transparent 22px, #D4C4B0 22px, #D4C4B0 23px)',
              pointerEvents: 'none'
            }} />
            <textarea
              value={(studentFields.resources_needed || []).join('\n')}
              onChange={(e) => handleArrayChange('resources_needed', e.target.value)}
              onBlur={saveData}
              placeholder="Resource..."
              style={{
                position: 'relative',
                zIndex: 1,
                width: '100%',
                height: '136px',
                border: 'none',
                background: 'transparent',
                fontSize: '13px',
                fontFamily: 'inherit',
                resize: 'none',
                lineHeight: '23px'
              }}
            />
          </div>
        </div>

        {/* Roles */}
        <div>
          <div style={{
            backgroundColor: '#4A90A4',
            color: 'white',
            padding: '8px',
            fontWeight: 'bold',
            fontSize: '11px',
            textAlign: 'center',
            marginBottom: '4px'
          }}>
            Roles of group members
          </div>
          <textarea
            value={studentFields.roles_of_group_members || ''}
            onChange={(e) => setStudentFields(prev => ({ ...prev, roles_of_group_members: e.target.value }))}
            onBlur={saveData}
            placeholder="Describe roles..."
            style={{
              width: '100%',
              backgroundColor: '#C5E3ED',
              padding: '12px',
              minHeight: '160px',
              border: '1px solid #4A90A4',
              fontSize: '13px',
              fontFamily: 'inherit',
              lineHeight: '1.5',
              resize: 'vertical'
            }}
          />
        </div>

        {/* Barriers */}
        <div>
          <div style={{
            backgroundColor: '#4A90A4',
            color: 'white',
            padding: '8px',
            fontWeight: 'bold',
            fontSize: '11px',
            textAlign: 'center',
            marginBottom: '4px'
          }}>
            Possible barriers to doing the project
          </div>
          <textarea
            value={(studentFields.possible_barriers || []).join('\n')}
            onChange={(e) => handleArrayChange('possible_barriers', e.target.value)}
            onBlur={saveData}
            placeholder="Barrier..."
            style={{
              width: '100%',
              backgroundColor: '#C5E3ED',
              padding: '12px',
              minHeight: '160px',
              border: '1px solid #4A90A4',
              fontSize: '13px',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
          />
        </div>
      </div>

      {/* Learner Reflection */}
      <div style={{
        backgroundColor: '#4A90A4',
        color: 'white',
        padding: '10px',
        fontWeight: 'bold',
        fontSize: '12px',
        marginBottom: '12px',
        textAlign: 'center'
      }}>
        Learner Reflection
      </div>

      <div style={{
        fontSize: '13px',
        marginBottom: '12px',
        fontWeight: 'bold'
      }}>
        Tick (✓) all statements that apply.
      </div>

      <div>
        {renderCheckboxSection('Awareness', awarenessOptions, 'awareness')}
        {renderCheckboxSection('Sensitivity', sensitivityOptions, 'sensitivity')}
        {renderCheckboxSection('Creativity', creativityOptions, 'creativity')}
      </div>
    </div>
  );
};

export default PartBPage2;
